import React from 'react'
import axiosSmartSurvey from '../../../api/axios'

import Swal from 'sweetalert2'
import useAuth from '../../../hooks/useAuth';

export default function useLandregistration() {
    const { user } = useAuth()

    const fetchGisParcelData = async (setRegistrationData, parcelCode, oldParcel, dn) => {
        try {
            // 1. เปิด Backdrop Loading ตรงกลางจอ
            Swal.fire({
                title: 'กำลังดึงข้อมูล...',
                text: 'กรุณารอซักครู่',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // 2. ยิง API
            const { data } = await axiosSmartSurvey.post('/landregistration/registrationdata', {
                parcelCode, oldParcel, dn
            });

            if (data && data.length > 0) {
                setRegistrationData(data);
                Swal.close(); // ปิด Loading ทันทีเมื่อข้อมูลมาแล้ว
            } else {
                setRegistrationData([]);

                // 🟡 แก้ไข: แจ้งเตือนตรงกลางหน้าจอเมื่อไม่พบข้อมูล
                Swal.fire({
                    icon: 'warning',
                    title: 'ไม่พบข้อมูลแปลงที่ดิน',
                    text: 'ไม่พบข้อมูลที่ตรงกับเงื่อนไขในระบบ พิกัดหรือแปลงที่ดินอาจไม่มีอยู่',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'ตกลง'
                });
            }

        } catch (err) {
            console.error(err);
            setRegistrationData([]);

            // 🔴 แก้ไข: แจ้งเตือนตรงกลางหน้าจอเมื่อระบบผิดพลาด
            Swal.fire({
                icon: 'error',
                title: 'เกิดข้อผิดพลาด',
                text: err.response?.data?.msg || 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง',
                confirmButtonColor: '#d33',
                confirmButtonText: 'รับทราบ'
            });
        }
    };

    // 🟢 ฟังก์ชันที่ถูกเรียกใช้ใน useEffect (สาดอาเรย์ประวัติทั้งหมดส่งตรงลงตาราง DataGrid)
    const fetchLandRegistryLog = async (setLandRegistrationData, rowData) => {
        try {
            const parcelCode = rowData?.parcel_cod;

            if (!parcelCode) {
                console.warn("⚠️ ไม่พบรหัส parcel_cod ในข้อมูลแถวที่ส่งมา");
                setLandRegistrationData([]); // เคลียร์ตารางเป็นว่างป้องกันแครช
                return;
            }

            // 🚀 ยิง POST ไปที่ท่อเดี่ยวหลังบ้าน MySQL เพื่อขอประวัติทั้งหมดของแปลงนี้ (เรียงล่าสุดอยู่บนสุดแล้ว)
            const response = await axiosSmartSurvey.post('/landregistration/logs', {
                parcelCode: parcelCode
            });

            // แกะซองพัสดุอาเรย์ออกมาใช้งาน
            const logsList = response.data.results || response.data;

            // ✨ ตรรกะถูกต้อง: สาดข้อมูลประวัติทั้งหมดลงอาเรย์ตาราง DataGrid
            if (Array.isArray(logsList) && logsList.length > 0) {

                // 🛡️ วนลูปสแกนล้างค่า null/undefined ทุกแถว ทุกคอลัมน์ ให้เป็นสตริงว่าง ''
                // ป้องกันปัญหากล่องข้อความและคอลัมน์ DataGrid ขึ้นข้อความฟ้องเออเรอร์
                logsList.forEach(row => {
                    Object.keys(row).forEach(key => {
                        if (row[key] === null || row[key] === undefined) {
                            row[key] = '';
                        }
                    });
                });

                // 🟢 โยนก้อนอาเรย์ที่คลีนแล้วทั้งหมด ลงไปสวมในตาราง DataGrid บนหน้าจอตรงๆ เลยครับ!
                setLandRegistrationData(logsList);
            } else {
                // 🔹 ถ้าแปลงนี้ยังไม่มีประวัติในระบบ MySQL เลย ให้ส่งอาเรย์ว่าง เพื่อให้ตารางขึ้นว่าไม่มีข้อมูล
                setLandRegistrationData([]);
            }

        } catch (err) {
            console.error("❌ Error inside fetchLandRegistryLog for DataGrid:", err);
            setLandRegistrationData([]); // เซฟตี้หากระบบเน็ตเวิร์กพัง ให้ตารางเคลียร์ว่างแอปไม่ค้าง
        }
    };

    async function fetchChangeLog() {
        try {
            const response = await axiosSmartSurvey({
                method: 'post',
                url: '/landregistration/logs'
            })
        } catch (err) {
            console.log(err)
        }
    }

    // 🟢 1. ฟังก์ชันดึงประวัติตัวเดี่ยวมาโชว์ในฟอร์ม (ตอนกดแก้ไข)
    const fetchSingleLandRegistryLog = async (id) => {
        try {
            // สมมติว่า URL หลังบ้านคือ /landregistration/log/:id
            const { data } = await axiosSmartSurvey.get(`/landregistration/log/${id}`);

            // ส่งข้อมูลแถวนั้นกลับไปให้ฟอร์ม (หน้าบ้านเราเช็ค data.results หรือข้อมูลดิบ)
            return data.results || data;
        } catch (err) {
            console.error("Fetch Single Log Error:", err);
            throw err; // ปล่อยให้คอมโพเนนต์จับ Error ไปพ่น Swal เอง
        }
    };

    // 🟢 ฟังก์ชันบันทึกข้อมูลเวอร์ชันดักจับช่องตัวเลข (ถ้าว่างให้ส่ง null ไปให้ MySQL)
    const saveLandRegistryLog = async (rowId, landRegistrationData) => {
        try {
            console.log(user)
            // 🛡️ ฟังก์ชันช่วยแปลงค่า: ถ้าเป็นสตริงว่าง หรือไม่ได้กรอก ให้ส่งค่า null กลับไปแทน
            const parseNumberOrNull = (value) => {
                if (value === undefined || value === null || String(value).trim() === '') {
                    return null;
                }
                return Number(value); // แปลงเป็นตัวเลขให้มั่นใจร้อยเปอร์เซ็นต์
            };

            // จัดเรียงพัสดุ Payload ใหม่
            const payload = {
                rowId: rowId,
                change_type: landRegistrationData.change_type,
                change_date: landRegistrationData.change_date,

                // 🗺️ ระเบียนที่ดินเดิม (ก่อนเปลี่ยน) -> ครอบระบบป้องกันช่องตัวเลขว่าง
                parcel_cod: landRegistrationData.parcel_cod,
                old_parcel: landRegistrationData.old_parcel,
                dn: landRegistrationData.dn,
                land_no: landRegistrationData.land_no,
                survey_no: landRegistrationData.survey_no,
                rai: parseNumberOrNull(landRegistrationData.rai),
                ngan: parseNumberOrNull(landRegistrationData.ngan),
                square_wa: parseNumberOrNull(landRegistrationData.square_wa),

                // 🗺️ ระเบียนที่ดินใหม่ (หลังเปลี่ยน) -> ครอบระบบป้องกันช่องตัวเลขว่าง
                new_parcel_cod: landRegistrationData.new_parcel_cod,
                new_old_parcel: landRegistrationData.new_old_parcel,
                new_dn: landRegistrationData.new_dn,
                new_land_no: landRegistrationData.new_land_no,
                new_survey_no: landRegistrationData.new_survey_no,
                new_rai: parseNumberOrNull(landRegistrationData.new_rai),
                new_ngan: parseNumberOrNull(landRegistrationData.new_ngan),
                new_square_wa: parseNumberOrNull(landRegistrationData.new_square_wa),

                // 👤 ฝั่งเจ้าของเดิม (ผู้โอน) + ที่อยู่
                old_owner_prefix: landRegistrationData.old_owner_prefix,
                old_owner_firstname: landRegistrationData.old_owner_firstname,
                old_owner_lastname: landRegistrationData.old_owner_lastname,
                old_owner_address_no: landRegistrationData.old_owner_address_no,
                old_owner_moo: landRegistrationData.old_owner_moo,
                old_owner_soi: landRegistrationData.old_owner_soi,
                old_owner_road: landRegistrationData.old_owner_road,
                old_owner_subdistrict: landRegistrationData.old_owner_subdistrict,
                old_owner_district: landRegistrationData.old_owner_district,
                old_owner_province: landRegistrationData.old_owner_province,
                old_owner_phone: landRegistrationData.old_owner_phone,

                // 🆕 ฝั่งเจ้าของใหม่ (ผู้รับโอน) + ที่อยู่
                new_owner_prefix: landRegistrationData.new_owner_prefix,
                new_owner_firstname: landRegistrationData.new_owner_firstname,
                new_owner_lastname: landRegistrationData.new_owner_lastname,
                new_owner_address_no: landRegistrationData.new_owner_address_no,
                new_owner_moo: landRegistrationData.new_owner_moo,
                new_owner_soi: landRegistrationData.new_owner_soi,
                new_owner_road: landRegistrationData.new_owner_road,
                new_owner_subdistrict: landRegistrationData.new_owner_subdistrict,
                new_owner_district: landRegistrationData.new_owner_district,
                new_owner_province: landRegistrationData.new_owner_province,
                new_owner_phone: landRegistrationData.new_owner_phone,

                note: landRegistrationData.note
            };
            console.log(payload)
            // ยิงกระสุน POST นัดเดียวทะลวงท่อหลังบ้านสไตล์มัดรวม
            const response = await axiosSmartSurvey.post('/landregistration/save-log', payload);
            console.log(response)
            if (response.data && response.data.status === 'OK') {
                return true;
            }
            return false;

        } catch (err) {
            console.error("Save Land Registry Log Error with Number Filter:", err);
            throw err;
        }
    };

    // ส่งฟังก์ชันออกไปให้ Component อื่นๆ เรียกใช้งาน
    return {
        fetchGisParcelData, fetchLandRegistryLog, fetchChangeLog,
        saveLandRegistryLog,
        fetchSingleLandRegistryLog
    }
}
